import { isEmpty, map, includes, compact } from 'lodash-es';
import moment from 'moment';
import { b as BxfDatePickerSeparatorsEnum, a as BxfDatePickerDateFormatEnum, c as BxfOverlayCalendarController } from './bxf-date-picker-separators.enum-a0b9baeb.js';
import { c as computeContentWidth } from './bxf-text-input.utils-6d1dfc5c.js';
import { Q as eventPath, P as WindowEventMap } from './bxf-utils-3908c3c5.js';
import { b as BxfOverlayTriggerEnum } from './overlay-controller-8ff05c99.js';
import { F as FocusIndexManager } from './focus-index-manager-d376b5ea.js';
import { B as BxfPositionConstants } from './focus-trap-023cd6d1.js';
import { K as KeyboardEventKey } from './keyboard-event-key.enum-2f6c4d91.js';

const { CLASSNAME, CALENDAR_CONTAINER, TODAY_MENU_CONTAINER, SETTINGS_CONTAINER, DROPDOWN_CONTAINER, } = BxfPositionConstants.STRING;
// CONSTANTS
const FOCUSED_CONTAINER = 'bxf-date-picker--container-focused';
const DATE_PICKER_KEY_FOCUSED = 'bxf-date-picker--focused-keyboard';
const SMALL = '0.22rem';
const REGULAR = '0.47rem';
const LARGE = '0.75rem';
const SPACE_REGEX = /\s/g;
const TODAY_RELATIVE = 'T';
/**
 * Determines if the input value is a relative value
 * @param value - The input value
 * @returns {boolean} Whether the input value is a relative value
 */
function isRelativeValue(value) {
    return typeof value === 'string' && value?.startsWith(TODAY_RELATIVE);
}
/**
 * Maps the date format to the BxfDatePickerDateFormatEnum by replacing the separators with spaces.
 * If the date format is not a valid BxfDatePickerDateFormatEnum, it will return the original date format.
 * @param dateFormat - The date format to map.
 * @returns {BxfDatePickerDateFormatEnum} The mapped date format enum value.
 */
function mapDateFormat(dateFormat) {
    if (isEmpty(dateFormat))
        return null;
    const newDateFormat = dateFormat
        .replace(/[-/.,]/g, BxfDatePickerSeparatorsEnum.SPACE)
        .trim();
    return Object.values(BxfDatePickerDateFormatEnum).includes(newDateFormat)
        ? newDateFormat
        : dateFormat;
}
function dateObjArraysEqual(valueA, valueB) {
    if (isEmpty(valueA) && isEmpty(valueB))
        return true;
    return isEmpty(valueA) || isEmpty(valueB) || valueA.length !== valueB.length
        ? false
        : valueA.findIndex((a, i) => a.day !== valueB[i].day ||
            a.month !== valueB[i].month ||
            a.year !== valueB[i].year) === -1;
}
/**
 * @returns { RegExp } separator value that has a global regular expression
 */
function getSeparatorRegex(separator) {
    return new RegExp(`\\${separator}`, 'g');
}
/**
 * Converts a date string to a moment object.
 * @param value - The date string to convert.
 * @param separator - The separator to use when converting the date string.
 * @param dateFormat - The date format to use when converting the date string.
 * @returns {moment.Moment | null} The moment object representing the date string,
 * or null if the date string is invalid.
 */
function strToMoment(value, separator, dateFormat) {
    return value
        ? moment(value, dateFormat.replace(SPACE_REGEX, separator), true)
        : null;
}
/**
 * Converts a date string to a calendar BxfCalendarDateInterface date object.
 * @param value - The date string to convert.
 * @param separator - The separator to use when converting the date string.
 * @param dateFormat - The date format to use when converting the date string.
 * @returns {BxfCalendarDateInterface[]} The date object representing the date string, or an empty array if the date string is invalid.
 */
function strToDateObj(value, separator, dateFormat) {
    if (!value)
        return [];
    const tempMoment = strToMoment(value, separator, dateFormat);
    return tempMoment.isValid()
        ? [
            {
                day: tempMoment.date(),
                month: tempMoment.month() + 1,
                year: tempMoment.year(),
            },
        ]
        : [];
}
/**
 * Determines if given value is in the proper date format with correct separator
 * @param value - The value to be checked
 * @param separator - The separator to use when converting the date string.
 * @param dateFormat - The date format to use when converting the date string.
 * @returns {boolean} - True if the value is in the proper date format, false otherwise
 */
function isValidDateFormat(value, separator, dateFormat) {
    const dateValue = (value?.match(/(?:\w+\W){2}\w+/) ?? [value])?.[0] ?? '';
    return (!value ||
        isRelativeValue(value) ||
        strToMoment(dateValue, separator, dateFormat)?.isValid());
}
/**
 * Returns the CSS class for the date picker container based on whether it has keyboard focus.
 * @param hasKeyboardFocus - Whether the date picker container has keyboard focus.
 * @returns {string} The CSS class for the date picker container.
 */
function getFocusCss(hasKeyboardFocus) {
    return hasKeyboardFocus ? DATE_PICKER_KEY_FOCUSED : FOCUSED_CONTAINER;
}
/**
 * Returns the left label offset for the date picker. So the instructional text and
 * error/success message can be aligned with the input field.
 * @param label - The label to calculate the offset for.
 * @param isCompact - Whether the date picker is compact.
 * @param isLeftLabel - Whether the label is on the left.
 * @param isRequired - Whether the date picker is required.
 * @returns {number} The left label offset for the date picker.
 */
function getLeftLabelOffset(label, isCompact, isLeftLabel, isRequired) {
    const paddingRight = isCompact ? 8 : 12;
    const isRequiredOffset = isRequired ? 12 : 0;
    return label && isLeftLabel
        ? +(/\d+/.exec(computeContentWidth(label)) ?? 0) +
            paddingRight +
            isRequiredOffset
        : 0;
}
/**
 * Updates the separator of a date format.
 * @param dateFormat - The date format to update.
 * @param oldSeparator - The old separator.
 * @param newSeparator - The new separator.
 * @returns {string} The updated date format.
 **/
function updateSeparator(dateFormat, oldSeparator, newSeparator) {
    const oldSeparatorRegex = new RegExp(`\\${oldSeparator}`, 'g');
    return dateFormat?.replace(oldSeparatorRegex, newSeparator);
}
/**
 * Corrects the date format of an input.
 * @param date - The date to correct.
 * @param oldDateFormat - The old date format.
 * @param newDateFormat - The new date format.
 * @param newSeparator - The new separator.
 * @returns {string} The corrected date.
 */
function updateFormatDate(date, oldDateFormat, newDateFormat, newSeparator) {
    const tempMoment = moment(date, updateSeparator(oldDateFormat, ' ', newSeparator), true);
    return tempMoment.isValid()
        ? tempMoment.format(updateSeparator(newDateFormat, ' ', newSeparator))
        : date;
}
/**
 * Returns the max or min date bound for the date component and calendar.
 * @param boundDate - The bound date.
 * @param boundYear - The bound year.
 * @param separator - The separator to use when converting the date string.
 * @param dateFormat - The date format to use when converting the date string.
 * @param isMinBound - Whether the bound is the minimum bound.
 * @returns {BxfCalendarDateInterface} The date bound for the date picker.
 */
function dateBoundHandler(boundDate, boundYear, separator, dateFormat, isMinBound) {
    if (boundDate) {
        return strToDateObj(boundDate, separator, dateFormat)[0];
    }
    if (boundYear) {
        return {
            day: isMinBound ? 1 : 31,
            month: isMinBound ? 1 : 12,
            year: boundYear,
        };
    }
    return {};
}
/**
 * Returns the padding top for the calendar button based on the date picker size.
 * So the calendar button is center-aligned with the input field.
 * @param size - The date picker size.
 * @returns {string} The padding top for the calendar button.
 */
function calendarButtonPaddingTop(size) {
    if (size === 'regular') {
        return REGULAR;
    }
    if (size === 'large') {
        return LARGE;
    }
    return SMALL;
}
/**
 * Converts older disabled dates interface to the new newer one.
 * for calendar.
 * @param dates - The disabled dates.
 * @param separator - The separator to use when converting the date string.
 * @param dateFormat - The date format to use when converting the date string.
 * @returns {BxfCalendarInterface[]} The mapped format of disabled dates for the calendar.
 */
function disabledDatesHandler(dates, separator, dateFormat) {
    if (!dates)
        return [];
    const disabledDates = Object.values(dates).reduce((acc, val) => acc.concat(val), []);
    return disabledDates?.length && typeof disabledDates[0] === 'string'
        ? disabledDates
            .map((date) => strToDateObj(date, separator, dateFormat)[0] ?? null)
            .filter((n) => n)
        : disabledDates;
}
/**
 * Adds zero padding to a number or string if it is less than 10.
 * @param val - The number or string to add zero padding to.
 * @returns The input value with zero padding if it is less than 10, otherwise the input value as is.
 */
function addZeroPadding(val) {
    return +val < 10 ? '0' : '';
}
/**
 * Checks if the event occurs is outside the overlay
 * @param event - Event
 **/
function isOutsideOverlay(event) {
    return (!map(eventPath(event), CLASSNAME).find((el) => includes(el, CALENDAR_CONTAINER)) &&
        !map(eventPath(event), CLASSNAME).find((el) => includes(el, SETTINGS_CONTAINER)) &&
        !map(eventPath(event), CLASSNAME).find((el) => includes(el, TODAY_MENU_CONTAINER)));
}
/**
 * Valid key input is when user types a character for the date it will fail if:
 *  - date format is one of our deprecated formats and user types a letter
 *  - key value has multiple characters or not a letter/number
 *  - value length is greater than date format length when date format does not include full month
 * @param key - string value of the key pressed
 * @param value - string value of the user input
 * */
function validKey(key, value, inputDateFormat) {
    return (/\w/g.test(key) &&
        key.length === 1 &&
        value.length === inputDateFormat.length &&
        !inputDateFormat.includes('MMMM'));
}
/**
 *  Initializes the overlay for the calendar
 *  @param props - BxfCalendarOverlayInterface
 *  @returns BxfOverlayCalendarController
 **/
function initOverlay(props) {
    const { host, ref, calendarRef, calendarButtonRef, overridePlacement, shouldOpen, shouldClose, keepMouseFocus, trigger, setIsOpen, sourceElementCustomClass, targetElementCustomClass, parentContainer, } = props;
    if (ref && calendarRef && calendarButtonRef) {
        return new BxfOverlayCalendarController(host, ref, ref, calendarRef, setIsOpen, {
            trigger,
            overlaysToClose: [DROPDOWN_CONTAINER],
            sourceElementOptions: {
                addClassNames: [...sourceElementCustomClass],
            },
            targetElementOptions: {
                addClassNames: [...targetElementCustomClass],
                targetForEventListeners: calendarButtonRef,
                targetEventType: [WindowEventMap.click],
            },
            modifiers: {
                defaultPlacement: overridePlacement,
            },
            shouldOpenCallback: shouldOpen,
            shouldCloseCallback: shouldClose,
            keepMouseFocus,
            shouldCloseOnMouseUp: trigger === BxfOverlayTriggerEnum.PRESSED,
            parentContainer,
        }, isOutsideOverlay);
    }
}
/**
 * Keys that are not valid for the date picker's input
 * but have other functionality on the component.
 * @param event - KeyboardEvent
 * @param value - string value of the user input
 * @returns {boolean} Whether the key is a helper key
 */
function isHelperKeys(event, value) {
    return ([
        ...FocusIndexManager.HORIZONTAL_NAV_KEYS,
        KeyboardEventKey.Tab,
        KeyboardEventKey.Shift,
    ].includes(event.key) ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        value?.startsWith(TODAY_RELATIVE) ||
        (!value && event.key === TODAY_RELATIVE));
}
/**
 * Converts a date value to a string.
 * @param value - The date value to convert.
 * @param year - The year to use when converting the date value.
 * @param month - The month to use when converting the date value.
 * @returns {string} The string representing the date value.
 */
function dateValueToString(value, year, month) {
    const m = value?.month ?? (month || 1);
    return `${addZeroPadding(value?.day)}${value?.day ?? '01'} ${addZeroPadding(m)}${m} ${value?.year ?? year}`;
}
/**
 * Converts an array of date values to an array of strings.
 * @param values - The date values to convert.
 * @param year - The year to use when converting the date values.
 * @param month - The month to use when converting the date values.
 * @returns {string[]} The strings representing the date values.
 */
function valuesToString(values, year, month) {
    if (isEmpty(values))
        return [];
    const newVal = [...values];
    return compact(newVal?.map((value) => dateValueToString(value, year, month)));
}
/**
 * Converts a date value to a string.
 * @param value - The date value to convert.
 * @param year - The year to use when converting the date value.
 * @param month - The month to use when converting the date value.
 * @returns {string} The string representing the date value.
 */
function getDateObjectFromEventStr(eventStr, separator = BxfDatePickerSeparatorsEnum.DASH, // default date picker separator is dash
dateFormat = BxfDatePickerDateFormatEnum.DD_MM_YYYY // event always emitted as day/month/year
) {
    return strToDateObj(eventStr, separator, dateFormat)?.[0];
}
/**
 * Converts a BxfCalendarDateInterface date value to a string.
 * @param value - The date BxfCalendarDateInterface value to convert.
 * @param year - The year to use when converting the date value.
 * @param month - The month to use when converting the date value.
 * @returns {string} The string representing the date value.
 */
function formatDateObj(value, displayDateFormat, separator) {
    const formattedSeparator = separator ?? BxfDatePickerSeparatorsEnum.DASH;
    return updateFormatDate(dateValueToString(value).replace(SPACE_REGEX, formattedSeparator), BxfDatePickerDateFormatEnum.DD_MM_YYYY, // dateValueToString always returns DD MM YYYY
    displayDateFormat ?? BxfDatePickerDateFormatEnum.DD_MMM_YYYY, formattedSeparator);
}

export { SPACE_REGEX as S, TODAY_RELATIVE as T, strToDateObj as a, isValidDateFormat as b, getFocusCss as c, dateObjArraysEqual as d, getLeftLabelOffset as e, updateFormatDate as f, getSeparatorRegex as g, dateBoundHandler as h, isRelativeValue as i, calendarButtonPaddingTop as j, disabledDatesHandler as k, isOutsideOverlay as l, mapDateFormat as m, initOverlay as n, isHelperKeys as o, dateValueToString as p, valuesToString as q, getDateObjectFromEventStr as r, strToMoment as s, formatDateObj as t, updateSeparator as u, validKey as v };
